"use client";

import { type JSX } from "react";
import NextImage from "next/image";
import {
  NextImage as JSSImage,
  RichText,
  Text,
} from "@sitecore-content-sdk/nextjs";
import {
  extractHiddenBlock,
  getConfiguredHeadingStyles,
} from "../../utils/styles";
import { ContactCardProps } from "components/contact-card/contact-card.props";
import styles from "./ContactCard.module.scss";

const DARK_BG_COLORS = ["bg-dark-blue", "bg-ecolab-blue"];

const computeIsDarkBg = (rawStyles: string | undefined): boolean => {
  const stylesArray = rawStyles ? rawStyles.split(" ") : [];
  if (stylesArray.length === 0) return false;
  const bgColor = DARK_BG_COLORS.find((cls) => stylesArray.includes(cls));
  return Boolean(
    (bgColor && bgColor !== "") || stylesArray.includes("text-white"),
  );
};

const ContactCardDefaultComponent = (props: ContactCardProps): JSX.Element => (
  <div
    className={`component contactCard ${props?.params?.styles ?? ""} ${
      props?.fields ? "" : "emptyFields"
    }`}
  >
    <div className="component-content">
      <span className="is-empty-hint">ContactCard</span>
    </div>
  </div>
);

export const ContactCardBlueTitle = (props: ContactCardProps): JSX.Element => {
  console.log("---->[ContactCardBlueTitle] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) {
    return <ContactCardDefaultComponent {...props} />;
  }

  const id = params?.RenderingIdentifier;
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-text-gray";
  const dividerStyle =
    params?.NoDivider === "1" ? "" : styles.contactCardHeaderHeader;
  const typeOfTitle1Tag = params?.TypeOfTitle1Tag?.toLowerCase();
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      id={id ?? undefined}
      className={`contact-card-header-blue-title-component px-(--spacing-sixteen) pb-0 pt-4 ${styles.contactCardHeaderBlueTitle} ${params?.Styles ?? ""}  ${hiddenBlockStyles}`}
      data-testid="contact-card-header-blue-title-component"
      data-role="contact-card-header-blue-title-component"
    >
      {fields.Image?.value?.src && !isPageEditing && (
        <NextImage
          src={fields?.Image?.value?.src ?? ""}
          alt={(fields?.Image?.value?.alt as string) ?? ""}
          className="w-full object-contain my-3 rounded-md"
          data-testid="contact-card-image"
          width={440}
          height={248}
        />
      )}
      {isPageEditing && (
        <JSSImage
          field={fields.Image}
          className="w-full object-contain my-3 rounded-md"
          data-testid="contact-card-image"
          imageParams={{ mw: 600 }}
        />
      )}
      {(fields.TitleLine1?.value || isPageEditing) && (
        <RichText
          tag={typeOfTitle1Tag || "h2"}
          field={fields.TitleLine1}
          className={`${dividerStyle} richtext-content relative ${
            isDarkBg ? "text-white" : "text-ecolab-blue"
          } ${
            typeOfTitle1Tag
              ? getConfiguredHeadingStyles(typeOfTitle1Tag)
              : "text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px]"
          }  tracking-normal font-medium m-0 pb-[15px] pr-(--spacing-sixteen) min-h-[1px]`}
          data-testid="title1"
        />
      )}
      {(fields.TitleLine2?.value || isPageEditing) && (
        <div>
          <Text
            tag="p"
            field={fields.TitleLine2}
            className={`richtext-content ${textColor} font-normal tracking-[0.4px] lg:tracking-[0.8px] mb-2.5 mt-5`}
            data-testid="title2"
          />
        </div>
      )}
      {(fields.SubTitle?.value || isPageEditing) && (
        <div>
          <Text
            tag="p"
            field={fields.SubTitle}
            className={`richtext-content ${textColor} font-normal tracking-[0.4px] lg:tracking-[0.8px] mb-2.5`}
            data-testid="sub-title"
          />
        </div>
      )}
      {(fields.Body?.value || isPageEditing) && (
        <RichText
          field={fields.Body}
          data-testid="body-text"
          className={`richtext-content ${textColor} font-normal tracking-[0.4px] lg:tracking-[0.8px] mb-2.5`}
        />
      )}
    </div>
  );
};
